import {
  ChangeDetectorRef,
  inject,
  OnDestroy,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export type TranslatableEntity = {
  id: number;
  name: string;
};

/**
 * Translates an entity's name by key `<prefix>.<id>` (e.g. `request_products.5`,
 * `request_ratings.5`). Falls back to the raw `entity.name` when no translation
 * is found for the current (or fallback) language, instead of showing the raw key.
 */
@Pipe({
  name: 'entityTranslate',
  pure: false,
})
export class EntityTranslatePipe implements PipeTransform, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private value = '';
  private lastEntity: TranslatableEntity | null = null;
  private lastPrefix: string | null = null;

  private readonly subscription: Subscription;

  constructor() {
    this.subscription = new Subscription();
    this.subscription.add(
      this.translateService.onLangChange.subscribe(() => this.updateValue()),
    );
    this.subscription.add(
      this.translateService.onFallbackLangChange.subscribe(() =>
        this.updateValue(),
      ),
    );
    this.subscription.add(
      this.translateService.onTranslationChange.subscribe(() =>
        this.updateValue(),
      ),
    );
  }

  transform(
    entity: Pick<TranslatableEntity, 'id' | 'name'> | null | undefined,
    prefix: string,
  ): string {
    if (!entity) {
      return '';
    }

    if (
      !this.lastEntity ||
      this.lastEntity.id !== entity.id ||
      this.lastEntity.name !== entity.name ||
      this.lastPrefix !== prefix
    ) {
      this.lastEntity = entity;
      this.lastPrefix = prefix;
      this.updateValue();
    }

    return this.value;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateValue(): void {
    if (!this.lastEntity || this.lastPrefix === null) {
      return;
    }

    const key = `${this.lastPrefix}.${this.lastEntity.id}`;
    const translated = this.translateService.instant(key);

    this.value =
      translated === key ? this.lastEntity.name : (translated as string);
    this.changeDetectorRef.markForCheck();
  }
}
